// Phase 44.04 — DeliveryPacksCard (wallet premium layout).
// Header sx (icona+titolo+sub), badge scadenza dx.
// Blocco centrale grande con saldo corse bonus + sub.
// CTA acquisto compatta: titolo + sub + prezzo a destra.
// Lista pack attivi con progress bar (padding ridotto).
import { useState } from "react";
import { Package, Calendar, History, ChevronDown, ChevronUp, Sparkles } from "lucide-react";
import { formatCHF, formatDateTime } from "@/lib/format";
import { validPriceTax } from "@/lib/priceTax";
import CustomerTaxBreakdown from "@/components/CustomerTaxBreakdown";

const STATUS_META = {
  active:           { label: "Attivo",   pill: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  pending_payment:  { label: "In attesa", pill: "bg-amber-50 text-amber-700 border-amber-200" },
  depleted:         { label: "Esaurito", pill: "bg-dapnow-bg text-dapnow-muted border-dapnow-line" },
  expired:          { label: "Scaduto",  pill: "bg-rose-50 text-rose-700 border-rose-200" },
  cancelled:        { label: "Annullato",pill: "bg-rose-50 text-rose-700 border-rose-200" },
};

function formatShortDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("it-CH", { day: "2-digit", month: "short", year: "2-digit" });
  } catch { return iso; }
}

export default function DeliveryPacksCard({ packs, catalog, onBuyPack, busy, disabled = false, business = false }) {
  const [historyOpen, setHistoryOpen] = useState(false);
  const totalRemaining = packs?.total_remaining || 0;
  const nextExpiring = packs?.next_expiring_at;
  const list = packs?.packs || [];
  const activePacks = list.filter((p) => p.status === "active");
  const tax = validPriceTax(catalog?.price_tax) ? catalog.price_tax : null;
  const available = Boolean(catalog) && (business ? tax?.audience === "business" : (!tax || tax.audience === "private"));
  const totalPrice = tax?.total_chf ?? catalog?.price_chf;

  return (
    <section
      data-testid="delivery-packs-card"
      className="card-soft p-5 sm:p-6 overflow-hidden"
    >
      {/* ── HEADER ────────────────────────────────────────────────────────── */}
      <header className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700 shrink-0 ring-1 ring-emerald-200">
            <Package className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <h2 className="font-display text-lg sm:text-xl font-bold tracking-tight leading-none whitespace-nowrap">
              Credito Consegne
            </h2>
            <p className="text-xs text-dapnow-muted mt-1 leading-none">
              Pacchetti extra attivi
            </p>
          </div>
        </div>
        {nextExpiring && totalRemaining > 0 && (
          <span
            className="shrink-0 inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[10px] font-semibold text-emerald-900"
            data-testid="pack-next-expiring"
          >
            <Calendar className="h-3 w-3" />
            Scade {formatShortDate(nextExpiring)}
          </span>
        )}
      </header>

      {/* ── BLOCCO CENTRALE (saldo) ──────────────────────────────────────── */}
      <div className="mt-4 rounded-2xl bg-emerald-50/60 border border-emerald-200/60 px-5 py-4" data-testid="pack-balance-block">
        <p className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-emerald-900 leading-none">
          {totalRemaining > 0
            ? `${totalRemaining} cors${totalRemaining === 1 ? "a" : "e"} bonus`
            : "Nessuna corsa bonus"}
        </p>
        <p className="text-xs text-emerald-800/80 mt-1.5">
          {totalRemaining > 0
            ? "Usate automaticamente dopo l'abbonamento."
            : "Acquista un pacchetto +10 per coprire le prossime consegne."}
        </p>
      </div>

      {/* ── CTA ACQUISTO (compatta, premium) ─────────────────────────────── */}
      <button
        type="button"
        onClick={onBuyPack}
        disabled={busy || disabled || !available}
        data-testid="delivery-pack-buy-btn"
        className="mt-4 w-full flex flex-wrap items-center gap-3 rounded-2xl bg-dapnow-ink text-white px-4 py-4 min-h-16 hover:bg-dapnow-blue transition shadow-md active:scale-[0.98] duration-150 disabled:opacity-60"
      >
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-white/15 shrink-0">
          <Sparkles className="h-4 w-4" />
        </span>
        <div className="flex-1 text-left min-w-0">
          <p className="font-display text-sm font-extrabold tracking-tight truncate">
            {busy ? "Attendi…" : "Pacchetto +10 corse"}
          </p>
          <p className="text-[11px] opacity-80 mt-0.5">
            {available ? `${formatCHF((business ? tax.net_chf : totalPrice) / catalog.total_rides)} per consegna, ${business ? "IVA esclusa" : "IVA inclusa"} · validità ${catalog.validity_months} mesi` : "Prezzo temporaneamente non disponibile"}
          </p>
        </div>
        <span className="font-display text-xl font-extrabold tabular-nums shrink-0" data-testid="delivery-pack-price">
          {available ? `Paga ${formatCHF(totalPrice)}` : "—"}
        </span>
      </button>
      {business && available && <CustomerTaxBreakdown tax={tax} className="mt-3" testId="delivery-pack-tax" />}

      {/* ── PACK ATTIVI (padding ridotto) ────────────────────────────────── */}
      {activePacks.length > 0 && (
        <div className="mt-4 space-y-1.5" data-testid="active-packs-list">
          {activePacks.map((p) => {
            const meta = STATUS_META[p.status] || STATUS_META.active;
            const progressPct = p.total_rides > 0
              ? Math.round(((p.total_rides - p.remaining_rides) / p.total_rides) * 100)
              : 0;
            return (
              <div key={p.id} className="rounded-xl border border-dapnow-line bg-white px-3 py-2" data-testid={`pack-row-${p.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 text-xs">
                    <span className="font-bold tabular-nums">{p.remaining_rides}/{p.total_rides}</span>
                    <span className={`inline-flex items-center rounded-full border px-1.5 py-0 text-[9px] font-semibold uppercase tracking-wide ${meta.pill}`}>{meta.label}</span>
                  </div>
                  <span className="text-[10px] text-dapnow-muted">
                    Scade {formatShortDate(p.expires_at)}
                  </span>
                </div>
                <div className="mt-1.5 h-1 rounded-full bg-emerald-50 overflow-hidden">
                  <div className="h-full bg-emerald-500 transition-all" style={{ width: `${progressPct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Storico utilizzo collassabile ────────────────────────────────── */}
      {list.length > 0 && (
        <div className="mt-3">
          <button
            type="button"
            onClick={() => setHistoryOpen(!historyOpen)}
            data-testid="pack-history-toggle"
            className="flex items-center gap-1.5 text-[11px] font-semibold text-dapnow-muted hover:text-dapnow-ink transition"
          >
            <History className="h-3 w-3" />
            {historyOpen ? "Nascondi" : "Mostra"} storico utilizzo
            {historyOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </button>
          {historyOpen && (
            <div className="mt-2 space-y-1.5 text-xs" data-testid="pack-history-list">
              {list.flatMap((p) => (p.usage_history || []).map((u) => ({ ...u, pack_id: p.id, catalog_id: p.catalog_id }))).length === 0 && (
                <p className="text-dapnow-muted italic">Nessun consumo registrato.</p>
              )}
              {list.flatMap((p) =>
                (p.usage_history || []).map((u) => ({ ...u, pack_id: p.id, catalog_id: p.catalog_id }))
              )
                .sort((a, b) => (b.at || "").localeCompare(a.at || ""))
                .slice(0, 10)
                .map((u, i) => (
                  <div key={`${u.order_id}-${i}`} className="flex items-center justify-between gap-2 rounded-lg border border-dapnow-line bg-dapnow-bg/40 px-2.5 py-1.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`inline-block h-1.5 w-1.5 rounded-full ${u.action === "restored" ? "bg-emerald-500" : "bg-dapnow-blue"}`} />
                      <span className="font-mono text-[10px] truncate">#{(u.order_id || "").slice(0, 8).toUpperCase()}</span>
                      <span className="text-dapnow-muted">{u.action === "restored" ? "+1 ripristinata" : "-1 utilizzata"}</span>
                    </div>
                    <span className="text-dapnow-muted text-[10px]">{formatDateTime(u.at)}</span>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
